import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  FaHome, 
  FaExclamationTriangle, 
  FaClock, 
  FaDollarSign, 
  FaChartLine,
  FaCalendarAlt,
  FaUsers,
  FaPlus,
  FaCreditCard,
  FaUserPlus
} from 'react-icons/fa';
import NewRentalContract from '../../Views/RentalManagement/NewRentalContract/NewRentalContract';
import RegisterPayment from '../../Views/RentalManagement/RegisterPayment/RegisterPayment';
import TenantManagement from '../../Views/RentalManagement/TenantManagement/TenantManagement';
import style from './RentalDashboard.module.css';

const RentalDashboard = ({ data }) => {
  const [activeModal, setActiveModal] = useState(null);
  const [enrichedPayments, setEnrichedPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tenantModal, setTenantModal] = useState({ show: false, tenants: [], loading: false });

  // Función para obtener inquilinos por rentalId
  const fetchTenants = async (rentalId) => {
    setTenantModal({ show: true, tenants: [], loading: true });
    try {
      const response = await axios.get(`/rentals/admin/${rentalId}/tenants`);
      setTenantModal({ 
        show: true, 
        tenants: response.data.data || [], 
        loading: false 
      });
    } catch (error) {
      console.error('Error obteniendo inquilinos:', error);
      setTenantModal({ 
        show: true, 
        tenants: [], 
        loading: false 
      });
    }
  };

  // Procesar datos directamente cuando cambien upcomingPayments
  useEffect(() => {
    if (data?.upcomingPayments?.length) {
      setEnrichedPayments(data.upcomingPayments);
    } else {
      setEnrichedPayments([]);
    }
  }, [data?.upcomingPayments]);
  
  if (!data || !data.summary) {
    return (
      <div className={style.dashboard}>
        <p className={style.noData}>No hay datos disponibles del dashboard</p>
      </div>
    );
  }

  const { summary, upcomingPayments = [] } = data;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  return (
    <div className={style.dashboard}>
      {/* Métricas principales */}
      <div className={style.metricsGrid}>
        <div className={`${style.metricCard} ${style.active}`}>
          <div className={style.cardHeader}>
            <FaHome className={style.cardIcon} />
            <h3>Contratos Activos</h3>
          </div>
          <div className={style.cardValue}>{summary.activeContracts}</div>
          <Link to="/alquileres/contratos" className={style.cardLink}>
            Ver todos →
          </Link>
        </div>

        <div className={`${style.metricCard} ${style.warning}`}>
          <div className={style.cardHeader}>
            <FaExclamationTriangle className={style.cardIcon} />
            <h3>Por Vencer</h3>
          </div>
          <div className={style.cardValue}>{summary.expiringContracts}</div>
          <Link to="/alquileres/por-vencer" className={style.cardLink}>
            Ver detalles →
          </Link>
        </div>

        <div className={`${style.metricCard} ${style.danger}`}>
          <div className={style.cardHeader}>
            <FaClock className={style.cardIcon} />
            <h3>Pagos Vencidos</h3>
          </div>
          <div className={style.cardValue}>{summary.overduePayments}</div>
          <div className={style.cardSubvalue}>
            {formatCurrency(summary.totalOverdue)}
          </div>
          <Link to="/alquileres/vencidos" className={style.cardLink}>
            Gestionar →
          </Link>
        </div>

        <div className={`${style.metricCard} ${style.pending}`}>
          <div className={style.cardHeader}>
            <FaCalendarAlt className={style.cardIcon} />
            <h3>Pendientes Este Mes</h3>
          </div>
          <div className={style.cardValue}>{summary.pendingPaymentsThisMonth}</div>
          <Link to="/alquileres/pendientes" className={style.cardLink}>
            Ver lista →
          </Link>
        </div>

        <div className={`${style.metricCard} ${style.success}`}>
          <div className={style.cardHeader}>
            <FaDollarSign className={style.cardIcon} />
            <h3>Cobrado Este Mes</h3>
          </div>
          <div className={style.cardValue}>
            {formatCurrency(summary.collectedThisMonth)}
          </div>
          <Link to="/alquileres/reportes" className={style.cardLink}>
            Ver reportes →
          </Link>
        </div>

        <div className={`${style.metricCard} ${style.info}`}>
          <div className={style.cardHeader}>
            <FaChartLine className={style.cardIcon} />
            <h3>Próximos Ajustes</h3>
          </div>
          <div className={style.cardValue}>{summary.upcomingAdjustments}</div>
          <Link to="/alquileres/ajustes" className={style.cardLink}>
            Gestionar ajustes →
          </Link>
        </div>
      </div>

      {/* Acciones rápidas */}
      <div className={style.quickActions}>
        <h3>Acciones Rápidas</h3>
        <div className={style.actionGrid}>
          <button 
            type="button"
            onClick={() => setActiveModal('contract')}
            className={style.actionButton}
          >
            <FaPlus /> Nuevo Contrato
          </button>
          {/* <Link to="/alquileres/inquilinos" className={style.actionButton}>
            <FaUserPlus /> Gestionar Inquilinos
          </Link>
          <Link to="/alquileres/reportes" className={style.actionButton}>
            <FaChartLine /> Ver Reportes
          </Link> */}
        </div>
      </div>

      {/* Próximos pagos */}
      {enrichedPayments.length > 0 && (
        <div className={style.upcomingPayments}>
          <h3>Próximos Pagos</h3>
          <div className={style.paymentsTable}>
            <div className={style.tableHeader}>
              <span>Propiedad</span>
              <span>Propietario</span>
              <span>Inquilinos</span>
              <span>Vencimiento</span>
              <span>Monto</span>
              <span>Estado</span>
            </div>
            {enrichedPayments.slice(0, 10).map((payment, index) => (
              <div key={payment.id || index} className={style.tableRow}>
                <div className={style.property}>
                  <div className={style.propertyTitle}>
                    ID: {payment.rental?.property?.id}
                  </div>
                  <div className={style.propertyDetails}>
                    <span className={style.propertyAddress}>
                      📍 {payment.rental?.property?.street} {payment.rental?.property?.number}, {payment.rental?.property?.locality}
                    </span>
                    <span className={style.propertyType}>
                      🏠 {payment.rental?.property?.propertyType}
                    </span>
                  </div>
                </div>
                <span className={style.owner}>
                  {payment.rental?.ownerName || 'Sin propietario'}
                </span>
                <div className={style.tenantActions}>
                  <button 
                    onClick={() => fetchTenants(payment.rentalId)}
                    className={style.tenantsButton}
                  >
                    Ver Inquilinos
                  </button>
                </div>
                <span className={style.dueDate}>
                  {formatDate(payment.dueDate)}
                </span>
                <span className={style.amount}>
                  {formatCurrency(payment.totalAmount)}
                </span>
                <span className={`${style.status} ${style[payment.paymentStatus]}`}>
                  {payment.paymentStatus === 'pending' ? 'Pendiente' : 
                   payment.paymentStatus === 'overdue' ? 'Vencido' : 
                   payment.paymentStatus === 'paid' ? 'Pagado' : 
                   payment.paymentStatus}
                </span>

              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal de Inquilinos */}
      {tenantModal.show && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <div className={style.modalHeader}>
              <h3>Lista de Inquilinos</h3>
              <button 
                onClick={() => setTenantModal({ show: false, tenants: [], loading: false })}
                className={style.closeButton}
              >
                ×
              </button>
            </div>
            <div className={style.modalContent}>
              {tenantModal.loading ? (
                <p>Cargando inquilinos...</p>
              ) : tenantModal.tenants.length > 0 ? (
                <div className={style.tenantsList}>
                  {tenantModal.tenants.map((tenant, index) => (
                    <div key={tenant.id || index} className={style.tenantItem}>
                      <div className={style.tenantInfo}>
                        <strong>{tenant.fullName || `${tenant.firstName} ${tenant.lastName}`}</strong>
                        <span className={style.tenantType}>
                          {tenant.tenantType === 'main' ? 'Inquilino Principal' : 
                           tenant.tenantType === 'guarantor' ? 'Garante' : 
                           tenant.tenantType === 'co-tenant' ? 'Co-inquilino' : 
                           tenant.tenantType}
                        </span>
                        {tenant.email && <span>📧 {tenant.email}</span>}
                        {tenant.phone && <span>📞 {tenant.phone}</span>}
                        {tenant.dni && <span>🆔 DNI: {tenant.dni}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No hay inquilinos registrados para este contrato.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modales de Acciones Rápidas */}
      {activeModal === 'contract' && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <NewRentalContract onClose={() => setActiveModal(null)} />
          </div>
        </div>
      )}

      {activeModal === 'payment' && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <RegisterPayment onClose={() => setActiveModal(null)} />
          </div>
        </div>
      )}

      {activeModal === 'tenant' && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <TenantManagement onClose={() => setActiveModal(null)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalDashboard;